import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarClock, MessageCircle, BookText, Layers, GraduationCap } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Icon from "../components/Icon";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { useLang } from "../i18n/LanguageContext";
import { useTranslatedPillar } from "../i18n/useTranslatedPillar";
import { SITE } from "../i18n/translations";
import { useBooking } from "../context/BookingContext";
import { getPillar, PILLARS } from "../data/pillars";

function Paras({ items }) {
  return (
    <div className="prose-tara">
      {items.map((p, i) => (
        <p key={i} dangerouslySetInnerHTML={{ __html: p.replace(/\*(.+?)\*/g, "<strong class='text-tara-sindoor font-semibold'>$1</strong>") }} />
      ))}
    </div>
  );
}

export default function PillarPage() {
  const { slug } = useParams();
  const { t, pick, lang } = useLang();
  const { openBooking } = useBooking();
  const pillar = getPillar(slug);
  const translated = useTranslatedPillar(slug);

  if (!pillar) return <Navigate to="/knowledge" replace />;

  const title = translated?.title || pick(pillar, "title");
  const summary = translated?.summary || pick(pillar, "summary");
  const body = {
    beginner: translated?.beginner?.length ? translated.beginner : pillar.beginner,
    detailed: translated?.detailed?.length ? translated.detailed : pillar.detailed,
    advanced: translated?.advanced?.length ? translated.advanced : pillar.advanced,
  };
  const faqs = translated?.faqs?.length ? translated.faqs : (pillar.faq || []);
  const related = (pillar.related || []).map(getPillar).filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: title,
        description: summary,
        image: pillar.image,
        datePublished: pillar.updated,
        dateModified: pillar.updated,
        author: { "@type": "Person", name: SITE.practitioner, url: `${SITE.domain}/about` },
        publisher: { "@type": "Organization", name: SITE.name, url: SITE.domain },
        mainEntityOfPage: `${SITE.domain}/knowledge/${pillar.slug}`,
      },
      {
        "@type": "FAQPage",
        mainEntity: (pillar.faq || []).map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title={title}
        description={summary}
        path={`/knowledge/${pillar.slug}`}
        image={pillar.image}
        type="article"
        keywords={pillar.keywords}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: t("nav_knowledge"), to: "/knowledge" }, { label: title }]} />

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-center gap-3 mb-4">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/70 border border-tara-gold/40">
            <Icon name={pillar.icon} className="h-5 w-5 text-tara-gold" />
          </span>
          {pillar.primary && <span className="rounded-full bg-tara-gold/15 border border-tara-gold/40 px-3 py-1 text-[10px] font-bold text-tara-gold tracking-wider uppercase">{t("core_pillar")}</span>}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink leading-tight mb-4">{title}</h1>
        <p className="font-display text-xl text-tara-body italic mb-4">{summary}</p>
        <p className="flex flex-wrap items-center gap-2 text-xs text-tara-muted">
          <CalendarClock className="h-3.5 w-3.5" /> {t("last_updated")}: {pillar.updated} · {SITE.practitioner}
          {lang !== "en" && translated && !translated.reviewed && (
            <span className="badge-gold rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em]" data-testid="pillar-ai-note">
              {t("ai_translated")}
            </span>
          )}
        </p>
      </motion.div>

      <div className="relative h-64 sm:h-80 rounded-2xl overflow-hidden my-8 border border-tara-gold/20">
        <img src={pillar.image} alt={title} className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14262A]/55 to-transparent" />
      </div>

      {/* Progressive content tabs */}
      <Tabs defaultValue="beginner" className="w-full" data-testid="pillar-tabs">
        <TabsList className="bg-white/70 border border-tara-gold/20 rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger value="beginner" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-tara-gold data-[state=active]:to-tara-vermilion data-[state=active]:text-white text-tara-body gap-1.5" data-testid="tab-beginner">
            <BookText className="h-4 w-4" /> {t("beginner")}
          </TabsTrigger>
          <TabsTrigger value="detailed" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-tara-gold data-[state=active]:to-tara-vermilion data-[state=active]:text-white text-tara-body gap-1.5" data-testid="tab-detailed">
            <Layers className="h-4 w-4" /> {t("detailed")}
          </TabsTrigger>
          <TabsTrigger value="advanced" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-tara-gold data-[state=active]:to-tara-vermilion data-[state=active]:text-white text-tara-body gap-1.5" data-testid="tab-advanced">
            <GraduationCap className="h-4 w-4" /> {t("advanced")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="beginner" className="mt-6"><Paras items={body.beginner} /></TabsContent>
        <TabsContent value="detailed" className="mt-6"><Paras items={body.detailed} /></TabsContent>
        <TabsContent value="advanced" className="mt-6"><Paras items={body.advanced} /></TabsContent>
      </Tabs>

      {/* Inline CTA */}
      <div className="glass-strong glass-reflect rounded-2xl p-6 sm:p-8 my-10 flex flex-col sm:flex-row items-center justify-between gap-5">
        <div>
          <h3 className="font-display text-2xl font-semibold text-tara-ink mb-1">Have a question about {title}?</h3>
          <p className="text-sm text-tara-muted">Start a guided enquiry and continue on WhatsApp with {SITE.practitioner}.</p>
        </div>
        <button onClick={() => openBooking("", `Question about ${title}`)} className="aura-pulse whitespace-nowrap flex items-center gap-2 rounded-full bg-gradient-to-r from-tara-gold to-tara-vermilion px-6 py-3.5 font-serif font-semibold text-white hover:brightness-110 transition" data-testid="pillar-cta-btn">
          <MessageCircle className="h-5 w-5" /> {t("cta_request")}
        </button>
      </div>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="my-10" data-testid="pillar-faq">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-tara-ink mb-5">{t("faq")}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="glass rounded-xl border-tara-gold/20 px-5" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-tara-ink font-display text-lg hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-tara-muted leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* References */}
      {pillar.references?.length > 0 && (
        <section className="my-10">
          <h2 className="font-serif text-xl font-semibold text-tara-ink mb-3">{t("references")}</h2>
          <ul className="space-y-2">
            {pillar.references.map((r, i) => (
              <li key={i} className="text-sm text-tara-muted pl-4 border-l-2 border-tara-gold/30">{r}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="my-10">
          <h2 className="font-serif text-xl font-semibold text-tara-ink mb-4">{t("related")}</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/knowledge/${r.slug}`} className="glass hover-lift rounded-xl p-4 block" data-testid={`related-${r.slug}`}>
                <Icon name={r.icon} className="h-5 w-5 text-tara-gold mb-2" />
                <p className="font-display text-lg text-tara-ink">{pick(r, "title")}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
